import * as React from 'react';
import { observer } from 'mobx-react';
import { translate } from 'elc-service';
import { Row } from 'elc-grid';
import styled from 'styled-components';
import { ContentSpacing } from 'elc-base-theme';
import { serviceNames, diContainer } from '../../../service-setup/diContainer';
import { ProductFiltersViewController } from '../../controllers/ProductFiltersViewController';
import { Filter } from '../../domain/entities/Filter';
import { classes } from '../../utils/Classes';
import { CategoryFiltersButton } from './components/CategoryFiltersItem';
import { FilterSet } from '../../domain/entities/FilterSet';
import { ProductsNo } from './components/ProductsNo';
import { observable, computed } from 'mobx';
import { FiltersList } from './components/FiltersList';
import { FiltersSets } from './components/FiltersSets';
import { OutlinedButton } from 'elc-buttons';
import { FilterIcon } from 'elc-icons';
import { IFilterConfig } from './FiltersConfig';
import { ClearAllFilters } from './components/ClearAllFilters';

const StyledButtonRow = styled(Row)`
    padding-top: ${ContentSpacing.space16};
`;

const StyledToggleButton = styled(OutlinedButton)``;

export interface IFiltersTranslation {
    filtersLabel: string;
    clearFiltersLabel: string;
    applyFiltersLabel: string;
    filterByCategoryLabel: string;
    filterByPriceLabel: string;
    resultsLabel: string;
    filterByLabel: string;
    removeIconAriaLabel: string;
    collapseIconAriaLabel: string;
    expandIconAriaLabel: string;
}

export interface IFiltersComponent {
    filterSets: FilterSet[];
    appliedFilters: FilterSet[];
    productsNumber: number;
    config: IFilterConfig;
    totalAppliedFilters?: number;
    translations?: IFiltersTranslation;
}

@translate(['clearFiltersLabel', 'filtersLabel'])
@observer
class DesktopFilters extends React.Component<IFiltersComponent> {
    @observable
    private productFiltersViewController: ProductFiltersViewController;

    constructor(props: IFiltersComponent) {
        super(props);

        this.productFiltersViewController = diContainer.get(
            serviceNames.productFiltersViewController
        );
    }

    private clearAllFilters = () => {
        this.productFiltersViewController.onClearAllAppliedFilters(this.props.appliedFilters);
    };

    @computed
    private get shouldHideFilterSets() {
        return (
            this.props.config.showToggler &&
            !this.productFiltersViewController.data.isFiltersModalOpen
        );
    }

    @computed
    private get shouldHideSelectedFilterSet() {
        return (
            !this.props.filterSets || !this.props.filterSets.length || this.props.config.showToggler
        );
    }

    private getAppliedFilters() {
        if (!this.props.appliedFilters.length || this.props.config.showToggler) {
            return null;
        }

        return (
            <>
                <FiltersList
                    productsNumber={this.props.productsNumber}
                    appliedFilters={this.props.appliedFilters}
                />
                {this.props.appliedFilters.length && (
                    <ProductsNo productsNumber={this.props.productsNumber} />
                )}
            </>
        );
    }

    private getSelectedFilterSet() {
        if (this.shouldHideSelectedFilterSet) {
            return null;
        }

        const styledButtonRowClassnames = classes('styled-filters-button-row');
        const { allowAllFiltersCollapsed } = this.props.config;
        let selectedFilterSet = this.props.filterSets.find(filterSet => filterSet.isOpen);

        if (!selectedFilterSet) {
            if (allowAllFiltersCollapsed) {
                return null;
            }

            selectedFilterSet = this.props.filterSets[0];
        }

        return (
            <StyledButtonRow className={styledButtonRowClassnames}>
                {selectedFilterSet.filters.map((option: Filter, keyNumber: number) => {
                    return (
                        <CategoryFiltersButton
                            onSelectFilterClick={option.toggleActiveState}
                            key={`${option.key}-${keyNumber}`}
                            isSelected={option.active}
                            label={option.label}
                            checkboxFilter={this.props.config.filtersAsCheckboxes}
                        />
                    );
                })}
            </StyledButtonRow>
        );
    }

    private handleFilterSetToggle = (filterSet: FilterSet) => {
        if (!filterSet) {
            return null;
        }

        const { singleFilterToggle, allowAllFiltersCollapsed } = this.props.config;
        const activeFilterSets = this.props.filterSets.filter(_filterSet => _filterSet.isOpen);

        if (singleFilterToggle) {
            const activeFilterClicked =
                activeFilterSets[0] && activeFilterSets[0].key === filterSet.key;

            if (activeFilterClicked && !allowAllFiltersCollapsed) {
                return null;
            }

            filterSet.setOpenState(true);
            activeFilterSets[0] && activeFilterSets[0].setOpenState(false);
        } else {
            filterSet.toggleOpenState();
        }
    };

    private getFilterSets() {
        const { filterSets } = this.props;

        if (this.shouldHideFilterSets) {
            return null;
        }

        return (
            <FiltersSets
                filterSets={filterSets}
                handleFilterSetToggle={this.handleFilterSetToggle}
                checkboxFilters={this.props.config.filtersAsCheckboxes}
                alwaysOn={this.props.config.alwaysToggled}
            />
        );
    }

    private toggleFilters = () => {
        if (this.productFiltersViewController.data.isFiltersModalOpen) {
            this.productFiltersViewController.onCloseFiltersClick();
        } else {
            this.productFiltersViewController.onOpenFiltersClick();
        }
    };

    private getFiltersToggle() {
        const filtersToggleClassNames = classes('mobile-filters-modal-wrapper');
        const { filtersLabel } = this.props.translations as IFiltersTranslation;

        if (!this.props.config.showToggler) {
            return null;
        }

        return (
            <StyledToggleButton className={filtersToggleClassNames} onClick={this.toggleFilters}>
                {filtersLabel}{' '}
                {this.props.totalAppliedFilters ? `(${this.props.totalAppliedFilters})` : ''}
                <FilterIcon />
            </StyledToggleButton>
        );
    }

    private getClearFiltersCTA() {
        if (!this.props.appliedFilters.length) {
            return null;
        }

        return <ClearAllFilters onClick={this.clearAllFilters} key="clear-all" />;
    }

    public render() {
        return (
            <>
                {this.getFiltersToggle()}
                {this.getFilterSets()}
                {this.getSelectedFilterSet()}
                {this.getAppliedFilters()}
                {this.getClearFiltersCTA()}
            </>
        );
    }
}

export { DesktopFilters };
